import * as XLSX from 'xlsx-js-style';

/**
 * Writes a sheet of rows to an .xlsx file and triggers the browser download.
 *
 * Uses `xlsx-js-style` rather than the plain `xlsx` already in the project:
 * SheetJS's community build silently DROPS cell styles on write, so bold
 * headings are impossible with it. `xlsx-js-style` is the same API plus honoured
 * `cell.s` styling — verified by inspecting the emitted xl/styles.xml.
 *
 * @param rows            array of arrays; ragged rows are fine
 * @param boldRows        row indexes to bold entirely (titles, column headers)
 * @param boldLabelRows   row indexes where only the first cell is a label to bold
 * @param fileName        e.g. "Acme-Goal-Achievers-Jul-2026-Report.xlsx"
 * @param sheetName       worksheet tab name
 */
export const downloadExcelSheet = ({
    rows = [],
    boldRows = [],
    boldLabelRows = [],
    fileName = 'report.xlsx',
    sheetName = 'Report',
}) => {
    const sheet = XLSX.utils.aoa_to_sheet(rows);
    const headingStyle = { font: { bold: true } };

    const bold = (rowIndex, colIndex) => {
        const address = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
        const cell = sheet[address];
        // Blank cells are absent from the sheet object entirely; nothing to style.
        if (!cell) return;
        cell.s = { ...(cell.s || {}), ...headingStyle };
    };

    boldRows.forEach((rowIndex) => {
        const width = rows[rowIndex]?.length || 0;
        for (let c = 0; c < width; c++) bold(rowIndex, c);
    });
    boldLabelRows.forEach((rowIndex) => bold(rowIndex, 0));

    // Size each column to its widest value so the report is readable without
    // the reader having to drag column borders around.
    const columnCount = rows.reduce((max, row) => Math.max(max, row?.length || 0), 0);
    sheet['!cols'] = Array.from({ length: columnCount }, (_, c) => {
        const longest = rows.reduce((max, row) => {
            const value = row?.[c];
            if (value === undefined || value === null) return max;
            return Math.max(max, String(value).length);
        }, 0);
        // Clamped: narrow enough to stay tidy, wide enough for an email address.
        return { wch: Math.min(Math.max(longest + 2, 10), 60) };
    });

    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, sheetName);
    // writeFile triggers the download in the browser.
    XLSX.writeFile(book, fileName);
};

export default downloadExcelSheet;
